import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { API } from "../App";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Loader2, Search, X, ArrowLeft, Users, MapPin,
  Building2, Droplet, Scale, Shield, GraduationCap, Zap,
  Factory, Landmark, Briefcase, Ship, Truck, Fuel, Anchor,
  HardHat, Sprout, Radio, TreePine, Warehouse, Building,
  Camera, PenLine, Printer, Download, PieChart as PieChartIcon,
} from "lucide-react";
import { toast } from "sonner";
import { QuestionarioReportDialog } from "./QuestionarioReportDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

/**
 * Infer a themed icon + tint for a posto based on its name prefix/keywords.
 * Falls back to a generic Building2 badge when no rule matches.
 */
function pickPostoStyle(name) {
  const n = (name || "").toUpperCase();
  const rules = [
    { re: /^EMBASA/,                            Icon: Droplet,       tint: "sky",      label: "Saneamento" },
    { re: /^PF\b|POLICIA FEDERAL/,              Icon: Shield,        tint: "slate",    label: "Polícia Federal" },
    { re: /^RECEITA FEDERAL/,                   Icon: Landmark,      tint: "emerald",  label: "Receita Federal" },
    { re: /^JUSTIÇA FEDERAL|JUSTICA FEDERAL/,   Icon: Scale,         tint: "indigo",   label: "Justiça Federal" },
    { re: /^MPT/,                               Icon: Scale,         tint: "purple",   label: "MPT" },
    { re: /^AGU/,                               Icon: Scale,         tint: "amber",    label: "AGU" },
    { re: /^DSEI/,                              Icon: TreePine,      tint: "green",    label: "DSEI" },
    { re: /^FUNAI/,                             Icon: TreePine,      tint: "orange",   label: "FUNAI" },
    { re: /^ENGIE/,                             Icon: Zap,           tint: "yellow",   label: "Engie" },
    { re: /^UFSB|UNIVASF|IF BAIANO|IF BAHIANO|IF BAIANO/, Icon: GraduationCap, tint: "rose", label: "Universidade" },
    { re: /^JOVEM APRENDIZ/,                    Icon: GraduationCap, tint: "pink",     label: "Jovem Aprendiz" },
    { re: /^SERPRO/,                            Icon: Radio,         tint: "cyan",     label: "SERPRO" },
    { re: /^CTI/,                               Icon: Radio,         tint: "teal",     label: "CTI" },
    { re: /^ULTRACARGO/,                        Icon: Fuel,          tint: "red",      label: "Ultracargo" },
    { re: /^IRON/,                              Icon: HardHat,       tint: "zinc",     label: "Iron" },
    { re: /^RMB/,                               Icon: Truck,         tint: "stone",    label: "RMB" },
    { re: /^CONSORCIO/,                         Icon: Factory,       tint: "amber",    label: "Consórcio" },
    { re: /^AVI\b/,                             Icon: Building2,     tint: "blue",     label: "AVI Interna" },
    { re: /^FILIAL/,                            Icon: Building2,     tint: "blue",     label: "Filial" },
    { re: /^ASSUFBA/,                           Icon: Building,      tint: "violet",   label: "ASSUFBA" },
    { re: /^ITEC/,                              Icon: Building,      tint: "fuchsia",  label: "ITEC" },
    { re: /^BOCAO/,                             Icon: Radio,         tint: "orange",   label: "Bocão News" },
  ];
  for (const r of rules) {
    if (r.re.test(n)) return r;
  }
  return { Icon: Briefcase, tint: "slate", label: "Outros" };
}

// Tailwind-safe tint classes (pre-declared so PurgeCSS keeps them)
const TINT_CLASSES = {
  sky:      { bg: "bg-sky-100",      text: "text-sky-700",      ring: "hover:ring-sky-300" },
  slate:    { bg: "bg-slate-200",    text: "text-slate-700",    ring: "hover:ring-slate-300" },
  emerald:  { bg: "bg-emerald-100",  text: "text-emerald-700",  ring: "hover:ring-emerald-300" },
  indigo:   { bg: "bg-indigo-100",   text: "text-indigo-700",   ring: "hover:ring-indigo-300" },
  purple:   { bg: "bg-purple-100",   text: "text-purple-700",   ring: "hover:ring-purple-300" },
  amber:    { bg: "bg-amber-100",    text: "text-amber-700",    ring: "hover:ring-amber-300" },
  green:    { bg: "bg-green-100",    text: "text-green-700",    ring: "hover:ring-green-300" },
  orange:   { bg: "bg-orange-100",   text: "text-orange-700",   ring: "hover:ring-orange-300" },
  yellow:   { bg: "bg-yellow-100",   text: "text-yellow-700",   ring: "hover:ring-yellow-300" },
  rose:     { bg: "bg-rose-100",     text: "text-rose-700",     ring: "hover:ring-rose-300" },
  pink:     { bg: "bg-pink-100",     text: "text-pink-700",     ring: "hover:ring-pink-300" },
  cyan:     { bg: "bg-cyan-100",     text: "text-cyan-700",     ring: "hover:ring-cyan-300" },
  teal:     { bg: "bg-teal-100",     text: "text-teal-700",     ring: "hover:ring-teal-300" },
  red:      { bg: "bg-red-100",      text: "text-red-700",      ring: "hover:ring-red-300" },
  zinc:     { bg: "bg-zinc-200",     text: "text-zinc-700",     ring: "hover:ring-zinc-300" },
  stone:    { bg: "bg-stone-200",    text: "text-stone-700",    ring: "hover:ring-stone-300" },
  blue:     { bg: "bg-blue-100",     text: "text-blue-700",     ring: "hover:ring-blue-300" },
  violet:   { bg: "bg-violet-100",   text: "text-violet-700",   ring: "hover:ring-violet-300" },
  fuchsia:  { bg: "bg-fuchsia-100",  text: "text-fuchsia-700",  ring: "hover:ring-fuchsia-300" },
};

/**
 * AdminPostosView — grid of postos (each posto = 1 tile with themed icon
 * and employee count). Clicking a tile drills down to the employees of
 * that posto (photo thumbnail + name/matrícula + signature/photo flags).
 *
 * Props:
 *  - onBack: called by the parent to switch back to the flat employees view
 */
export default function AdminPostosView({ onBack }) {
  const [postos, setPostos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedPosto, setSelectedPosto] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axios.get(`${API}/admin/postos`, { timeout: 20000 });
        if (!cancelled) setPostos(r.data?.postos || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Falha ao carregar postos");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredPostos = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return postos;
    return postos.filter((p) => (p.posto || "").toLowerCase().includes(q));
  }, [postos, search]);

  if (selectedPosto) {
    return (
      <PostoEmployeesList
        posto={selectedPosto}
        onBack={() => setSelectedPosto(null)}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="postos-view">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-slate-700" />
            Postos de Serviço
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {postos.length} postos · Clique num posto para ver seus funcionários
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onBack}
          data-testid="postos-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Voltar
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <Input
          placeholder="Buscar posto..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 pr-8 h-9"
          data-testid="postos-search-input"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-16 text-center">
          <Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" />
        </div>
      ) : error ? (
        <div className="py-8 text-center text-sm text-red-600">{error}</div>
      ) : filteredPostos.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-500">
          Nenhum posto encontrado.
        </div>
      ) : (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3"
          data-testid="postos-grid"
        >
          {filteredPostos.map((p) => {
            const { Icon, tint, label: groupLabel } = pickPostoStyle(p.posto);
            const tw = TINT_CLASSES[tint] || TINT_CLASSES.slate;
            return (
              <motion.button
                key={p.posto}
                type="button"
                onClick={() => setSelectedPosto(p.posto)}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                className={`bg-white border border-slate-200 rounded-xl p-3 text-left transition-all hover:shadow-md ring-2 ring-transparent ${tw.ring}`}
                data-testid={`posto-card-${p.posto}`}
              >
                <div className="flex items-start gap-2 mb-2">
                  <div className={`w-10 h-10 rounded-lg ${tw.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-5 h-5 ${tw.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wide leading-none">
                      {groupLabel}
                    </p>
                    <p className="text-[11px] font-semibold text-slate-900 leading-tight mt-0.5 line-clamp-2 break-words">
                      {p.posto}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-1 pt-2 border-t border-slate-100">
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-800">
                    <Users className="w-3 h-3" />
                    {p.count}
                  </span>
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                    <span
                      className="inline-flex items-center gap-0.5"
                      title="Fotos capturadas"
                    >
                      <Camera className="w-3 h-3 text-emerald-600" />
                      {p.with_photo}
                    </span>
                    <span
                      className="inline-flex items-center gap-0.5"
                      title="Rúbricas cadastradas"
                    >
                      <PenLine className="w-3 h-3 text-sky-600" />
                      {p.with_signature}
                    </span>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * PostoTrainingPackage — dialog to download the merged lista de
 * presença + certificados of a course/month for THIS posto only,
 * aggregating across separate attendance lists.
 */
function PostoTrainingPackage({ posto }) {
  const [open, setOpen] = useState(false);
  const [courses, setCourses] = useState([]);
  const [courseKey, setCourseKey] = useState("");
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [working, setWorking] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const isQuestionario = courseKey === "questionario_psicossocial";

  useEffect(() => {
    if (!open || courses.length) return;
    axios
      .get(`${API}/admin/courses`, { timeout: 15000 })
      .then((r) => {
        const items = (r.data || []).filter(
          (c) => c.course_key !== "ordem_servico"
        );
        setCourses(items);
        if (items.length && !courseKey) setCourseKey(items[0].course_key);
      })
      .catch(() => toast.error("Erro ao carregar cursos"));
  }, [open, courses.length, courseKey]);

  const authHeader = {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  };

  const triggerBlobDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleGenerate = async () => {
    if (!courseKey) {
      toast.error("Escolha o curso");
      return;
    }
    setWorking(true);
    setStatusMsg("Localizando listas do posto…");
    try {
      const start = await axios.post(
        `${API}/admin/courses/${courseKey}/posto-package-async`,
        { posto, month },
        { headers: authHeader, timeout: 30000 }
      );
      const jobId = start.data.job_id;
      setStatusMsg(
        `Gerando ${start.data.total} lista(s) + certificados em segundo plano…`
      );
      let attempts = 0;
      while (attempts < 150) {
        await new Promise((r) => setTimeout(r, 4000));
        attempts += 1;
        const st = await axios.get(`${API}/admin/courses/jobs/${jobId}`, {
          headers: authHeader,
        });
        if (st.data.status === "done") break;
        if (st.data.status === "failed")
          throw new Error(st.data.error || "Falha na geração");
        const prog = st.data.progress;
        if (prog)
          setStatusMsg(`Processando lista ${prog.current} de ${prog.total}…`);
      }
      if (attempts >= 150) throw new Error("Tempo limite excedido (10 min)");
      setStatusMsg("Baixando arquivos…");
      const safePosto = posto.replace(/[^A-Za-z0-9._-]+/g, "_");
      for (const kind of ["listas", "certs"]) {
        try {
          const dl = await axios.get(
            `${API}/admin/courses/jobs/${jobId}/download-bulk/${kind}`,
            { responseType: "blob", timeout: 180000, headers: authHeader }
          );
          if (dl.data?.size > 100) {
            triggerBlobDownload(
              new Blob([dl.data], { type: "application/zip" }),
              `${kind}_${courseKey}_${safePosto}_${month}.zip`
            );
            await new Promise((r) => setTimeout(r, 600));
          }
        } catch (_) {
          /* certs podem não existir para o curso — ignora */
        }
      }
      toast.success("Pacote do posto baixado!");
      setOpen(false);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e?.message || "Erro ao gerar pacote"
      );
    } finally {
      setWorking(false);
      setStatusMsg("");
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="text-indigo-700 border-indigo-200 hover:bg-indigo-50"
        data-testid="posto-training-package-btn"
      >
        <GraduationCap className="w-4 h-4 mr-2" />
        Treinamentos do posto
      </Button>
      <Dialog open={open} onOpenChange={(o) => !working && setOpen(o)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-indigo-600" />
              Treinamentos — {posto}
            </DialogTitle>
            <DialogDescription>
              Baixa a lista de presença e os certificados de um curso/mês
              apenas com os funcionários deste posto, mesmo que estejam em
              listas separadas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">
                Curso
              </label>
              <select
                value={courseKey}
                onChange={(e) => setCourseKey(e.target.value)}
                className="w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
                data-testid="posto-package-course-select"
              >
                {courses.map((c) => (
                  <option key={c.course_key} value={c.course_key}>
                    {c.titulo}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">
                Mês do treinamento
              </label>
              <Input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                data-testid="posto-package-month-input"
              />
            </div>
            {isQuestionario && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
                <p className="text-xs text-amber-900">
                  Curso anual: veja o gráfico das respostas anônimas deste
                  posto no ano de <strong>{month.slice(0, 4)}</strong>.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setReportOpen(true)}
                  className="w-full text-amber-800 border-amber-300 hover:bg-amber-100"
                  data-testid="posto-questionario-report-btn"
                >
                  <PieChartIcon className="w-4 h-4 mr-2" />
                  Ver gráfico do posto
                </Button>
              </div>
            )}
            {working && statusMsg && (
              <p className="text-xs text-indigo-700 flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {statusMsg}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={working}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={working || !courseKey}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="posto-package-generate-btn"
            >
              {working ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Baixar listas + certificados
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <QuestionarioReportDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        postoFixo={posto}
        year={parseInt(month.slice(0, 4), 10)}
      />
    </>
  );
}

/**
 * PostoEmployeesList — after the admin picks a posto, shows the employees
 * of that posto with a thumbnail, name, matrícula and quick status badges.
 */
function PostoEmployeesList({ posto, onBack }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const now = new Date();
        const params = new URLSearchParams({
          page: "1",
          per_page: "200",
          posto,
          access_year: String(now.getFullYear()),
          access_month: String(now.getMonth() + 1),
          sort_field: "nome",
          sort_order: "asc",
        });
        const r = await axios.get(`${API}/admin/employees?${params}`, { timeout: 30000 });
        if (!cancelled) setEmployees(r.data?.employees || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Falha ao carregar funcionários");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [posto]);

  const { Icon, tint, label: groupLabel } = pickPostoStyle(posto);
  const tw = TINT_CLASSES[tint] || TINT_CLASSES.slate;

  return (
    <div className="space-y-4" data-testid="posto-employees-view">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-11 h-11 rounded-xl ${tw.bg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-5 h-5 ${tw.text}`} />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] text-slate-500 uppercase tracking-wide">
              {groupLabel}
            </p>
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 truncate">
              {posto}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {employees.length} funcionário(s) neste posto
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onBack}
          data-testid="posto-employees-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Todos os postos
        </Button>
      </div>

      <div className="flex items-center justify-end gap-2">
        <PostoTrainingPackage posto={posto} />
        <Button
          size="sm"
          disabled={downloadingPdf || employees.length === 0}
          onClick={async () => {
            setDownloadingPdf(true);
            try {
              const r = await axios.get(`${API}/admin/badges/batch.pdf`, {
                params: { posto, _t: Date.now() },
                responseType: "blob",
                timeout: 300000,
              });
              const url = URL.createObjectURL(r.data);
              const a = document.createElement("a");
              const safe = (posto || "lote").replace(/[^A-Za-z0-9._-]+/g, "_");
              a.href = url;
              a.download = `crachas_${safe}.pdf`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
            } catch (e) {
              alert(e?.response?.data?.detail || "Falha ao gerar PDF de crachás");
            } finally {
              setDownloadingPdf(false);
            }
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="download-badges-pdf-btn"
        >
          {downloadingPdf ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Gerando PDF…
            </>
          ) : (
            <>
              <Printer className="w-4 h-4 mr-2" />
              Baixar crachás (PDF · 9 por página)
            </>
          )}
        </Button>
      </div>

      {loading ? (
        <div className="py-16 text-center">
          <Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" />
        </div>
      ) : error ? (
        <div className="py-8 text-center text-sm text-red-600">{error}</div>
      ) : employees.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-500">
          Nenhum funcionário neste posto.
        </div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2"
          data-testid="posto-employees-grid"
        >
          {employees.map((e) => (
            <Card key={e.id} className="border-slate-200 hover:border-slate-300 transition-colors">
              <CardContent className="p-3 flex items-center gap-3">
                {e.photo_3x4_base64 ? (
                  <img
                    src={e.photo_3x4_base64}
                    alt={e.nome}
                    className="w-11 h-14 object-cover rounded border border-slate-200 shadow-sm bg-white flex-shrink-0"
                  />
                ) : (
                  <div className="w-11 h-14 rounded border border-slate-200 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-[11px] font-bold text-slate-500 uppercase flex-shrink-0">
                    {(e.nome || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0] || "").join("")}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate" title={e.nome}>
                    {e.nome}
                  </p>
                  <p className="text-xs font-mono text-blue-600">{e.matricula}</p>
                  <p className="text-[10px] font-mono text-slate-400 truncate">{e.cpf_masked}</p>
                </div>
                <div className="flex flex-col items-end gap-1 text-[10px]">
                  <span
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                      e.has_photo_3x4
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-slate-50 text-slate-400 border border-slate-200"
                    }`}
                    title="Foto 3x4"
                  >
                    <Camera className="w-3 h-3" />
                    {e.has_photo_3x4 ? "✓" : "—"}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                      e.has_signature
                        ? "bg-sky-50 text-sky-700 border border-sky-200"
                        : "bg-slate-50 text-slate-400 border border-slate-200"
                    }`}
                    title="Rúbrica"
                  >
                    <PenLine className="w-3 h-3" />
                    {e.has_signature ? "✓" : "—"}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
